// src/components/ecommerce/InventoryMovementModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X,
  History,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Calendar,
  User,
  Package,
  FileText,
  Download,
  Filter,
  Search,
  RefreshCw
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';

export const InventoryMovementModal = ({ product, isOpen, onClose, onUpdate }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');

  useEffect(() => {
    if (isOpen && product) {
      loadMovements();
    }
  }, [isOpen, product]);

  const loadMovements = async () => {
    setLoading(true);
    try {
      // Load movements for this specific product
      const movementsData = await ecommerceService.getProductMovements(product.id);
      setMovements(movementsData);
    } catch (error) {
      console.error('Error loading movements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'in': return ArrowUp;
      case 'out': return ArrowDown;
      case 'adjustment': return RotateCcw;
      case 'transfer': return Package;
      default: return History;
    }
  };

  const getMovementColor = (type) => {
    switch (type) {
      case 'in': return 'text-green-600 bg-green-100 border-green-200';
      case 'out': return 'text-red-600 bg-red-100 border-red-200';
      case 'adjustment': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'transfer': return 'text-purple-600 bg-purple-100 border-purple-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getQuantityDisplay = (movement) => {
    const prefix = movement.type === 'in' ? '+' : movement.type === 'out' ? '-' : '±';
    return `${prefix}${movement.quantity}`;
  };

  const filteredMovements = movements.filter(movement => {
    const matchesType = filter === 'all' || movement.type === filter;
    const matchesSearch = movement.reason?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         movement.createdBy?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDate = dateRange === 'all' || (() => {
      const movementDate = new Date(movement.createdAt?.toDate?.() || movement.createdAt);
      const now = new Date();
      
      switch (dateRange) {
        case 'today':
          return movementDate.toDateString() === now.toDateString();
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return movementDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return movementDate >= monthAgo;
        default:
          return true;
      }
    })();
    
    return matchesType && matchesSearch && matchesDate;
  });

  const exportMovements = () => {
    const csvContent = [
      ['Date', 'Type', 'Quantity', 'Previous Stock', 'New Stock', 'Reason', 'User'].join(','),
      ...filteredMovements.map(movement => [
        new Date(movement.createdAt?.toDate?.() || movement.createdAt).toLocaleDateString(),
        movement.type,
        getQuantityDisplay(movement),
        movement.previousStock || '',
        movement.newStock || '',
        movement.reason || '',
        movement.createdBy || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${product.name}_movements.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <History className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Movement History</h2>
              <p className="text-gray-600">{product?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={exportMovements}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Download className="h-4 w-4" />
              Export
            </button>
            
            <button
              onClick={loadMovements}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 bg-gray-200 rounded-lg flex items-center justify-center">
              {product?.images?.[0] || product?.imageUrl ? (
                <img
                  src={product.images?.[0] || product.imageUrl}
                  alt={product.name}
                  className="h-16 w-16 object-cover rounded-lg"
                />
              ) : (
                <Package className="h-8 w-8 text-gray-400" />
              )}
            </div>
            
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{product?.name}</h3>
              <p className="text-gray-600">SKU: {product?.sku}</p>
              <p className="text-sm text-gray-500">Current Stock: {product?.quantity || 0} units</p>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Movements</p>
              <p className="text-2xl font-bold text-gray-900">{movements.length}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search movements..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="in">Stock In</option>
                <option value="out">Stock Out</option>
                <option value="adjustment">Adjustment</option>
                <option value="transfer">Transfer</option>
              </select>
              
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Movements List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredMovements.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredMovements.map(movement => {
                const MovementIcon = getMovementIcon(movement.type);
                const colorClasses = getMovementColor(movement.type);
                
                return (
                  <div key={movement.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg border ${colorClasses}`}>
                        <MovementIcon className="h-5 w-5" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClasses}`}>
                              {movement.type.charAt(0).toUpperCase() + movement.type.slice(1)}
                            </span>
                            <span className="font-medium text-gray-900">
                              {getQuantityDisplay(movement)} units
                            </span>
                          </div>
                          
                          <div className="text-right text-sm text-gray-500">
                            <div>{new Date(movement.createdAt?.toDate?.() || movement.createdAt).toLocaleDateString()}</div>
                            <div>{new Date(movement.createdAt?.toDate?.() || movement.createdAt).toLocaleTimeString()}</div>
                          </div>
                        </div>
                        
                        {movement.reason && (
                          <p className="text-gray-600 mb-2">{movement.reason}</p>
                        )}
                        
                        <div className="flex items-center gap-6 text-sm text-gray-500">
                          {movement.previousStock !== undefined && (
                            <span>Previous: {movement.previousStock} units</span>
                          )}
                          {movement.newStock !== undefined && (
                            <span>New: {movement.newStock} units</span>
                          )}
                          {movement.createdBy && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {movement.createdBy}
                            </span>
                          )}
                        </div>
                        
                        {movement.notes && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
                            <FileText className="h-3 w-3 inline mr-1" />
                            {movement.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No movements found</h3>
              <p className="text-gray-600">
                {searchQuery || filter !== 'all' || dateRange !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'No inventory movements recorded for this product'
                }
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {filteredMovements.length} of {movements.length} movements
            </p>
            
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};